import { proxy } from 'valtio';
import type { LayerWithUrl } from './useApiImap';

interface PublicLayersState {
  allLayers: LayerWithUrl[];
  selectedLayer: LayerWithUrl | null;
  selectedUrl: string | null;
}

interface AlkisToggleState {
  enabled: boolean;
}

/**
 * Valtio state for managing public layers in ALKIS Map
 * Used across multiple components for layer selection and WMS URL fetching
 */
export const publicLayersState = proxy<PublicLayersState>({
  allLayers: [],
  selectedLayer: null,
  selectedUrl: null,
});

/**
 * Valtio state for managing ALKIS overlay toggle
 * Used to control whether ALKIS layers should be displayed as overlay
 */
export const alkisToggleState = proxy<AlkisToggleState>({
  enabled: false,
});

const getDefaultLayer = (layers: LayerWithUrl[]): LayerWithUrl | null => {
  if (layers.length === 0) return null;
  return layers.find((l) => Boolean(l.url && (l.layerName || l.name))) || layers[0];
};

/**
 * Set all available public layers
 */
export const setPublicLayers = (layers: LayerWithUrl[]): void => {
  // Check if layers actually changed to avoid triggering unnecessary Valtio subscriber updates
  const currentLayers = publicLayersState.allLayers;
  const isIdentical =
    currentLayers.length === layers.length &&
    currentLayers.every(
      (l, i) =>
        l.url === layers[i].url &&
        (l.layerName || l.name) === (layers[i].layerName || layers[i].name),
    );

  if (isIdentical && publicLayersState.selectedLayer) {
    return;
  }

  publicLayersState.allLayers = layers;

  // Retain matching layer reference if still present, otherwise select default
  const existing = publicLayersState.selectedLayer
    ? layers.find(
        (l) =>
          (l.id && l.id === publicLayersState.selectedLayer?.id) ||
          ((l.layerName || l.name) ===
            (publicLayersState.selectedLayer?.layerName || publicLayersState.selectedLayer?.name) &&
            l.url === publicLayersState.selectedLayer?.url),
      )
    : undefined;

  if (existing) {
    publicLayersState.selectedLayer = existing;
    publicLayersState.selectedUrl = existing.url || null;
  } else if (layers.length > 0) {
    const defaultLayer = getDefaultLayer(layers);
    publicLayersState.selectedLayer = defaultLayer;
    publicLayersState.selectedUrl = defaultLayer?.url || null;
  } else {
    publicLayersState.selectedLayer = null;
    publicLayersState.selectedUrl = null;
  }
};

/**
 * Set the currently selected layer and its corresponding URL
 */
export const setSelectedLayer = (layer: LayerWithUrl | null): void => {
  publicLayersState.selectedLayer = layer;
  publicLayersState.selectedUrl = layer?.url || null;
};

/**
 * Toggle ALKIS overlay on/off
 */
export const setAlkisEnabled = (enabled: boolean): void => {
  alkisToggleState.enabled = enabled;

  // When enabling ALKIS, ensure the first available layer is selected if none currently is
  if (enabled && !publicLayersState.selectedLayer && publicLayersState.allLayers.length > 0) {
    const defaultLayer = getDefaultLayer(publicLayersState.allLayers);
    if (defaultLayer) {
      publicLayersState.selectedLayer = defaultLayer;
      publicLayersState.selectedUrl = defaultLayer.url || null;
    }
  }
};

/**
 * Clear all layers and selection
 */
export const clearLayers = (): void => {
  publicLayersState.allLayers = [];
  publicLayersState.selectedLayer = null;
  publicLayersState.selectedUrl = null;
};
